/* eslint-disable prettier/prettier */
import moment from 'moment'
import React, { Suspense, useEffect, useState } from 'react'
import { HashRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom'

import ThemeProvider from '../contexts/theme'

import ErrorBoundary from '../components/ErrorBoundary'
import Loading from '../components/CustomUI/Loading'
import ConfirmEmail from '../views/auth/ConfirmEmail'
import EmailVerification from '../views/auth/EmailVerification'
import LockScreen from '../views/auth/LockScreen'
import Login from '../views/auth/Login'
import RecoverPassword from '../views/auth/RecoverPassword'
import Register from '../views/auth/Register'
import VerifyEmail from '../views/auth/VerifyEmail'
import ResetPassword from '../views/auth/ResetPassword'
import ResendVerification from '../views/auth/ResendVerification'
import AcceptInvite from '../views/pages/AcceptInvite'
import Page404 from '../views/pages/Page404'
import Page500 from '../views/pages/Page500'
import Support from '../views/pages/Support'
import FAQs from '../views/pages/Support/FAQs'
import GettingStarted from '../views/pages/Support/GettingStarted'
import InstallationInstructions from '../views/pages/Support/InstallationInstructions'
import ProductSpecifications from '../views/pages/Support/ProductSpecifications'
import RegularUpdates from '../views/pages/Support/RegularUpdates'
import ServerRequirements from '../views/pages/Support/ServerRequirements'
import TroubleshootingTips from '../views/pages/Support/TroubleshootingTips'

const isAuthenticated = () => {
  // Check JWT token first
  const token = localStorage.getItem('token')
  if (token) {
    try {
      // Decode JWT to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]))
      if (payload.exp * 1000 > Date.now()) {
        return true
      }
      // Token expired
      localStorage.removeItem('token')
      localStorage.removeItem('user')
    } catch (e) {
      // Invalid token
      localStorage.removeItem('token')
      localStorage.removeItem('user')
    }
  }

  // Fallback to legacy token
  const now = moment().valueOf()
  const storage = JSON.parse(localStorage.getItem('cloveraiToken')) || null

  if (!storage) return false
  if (now > storage?.expires) {
    localStorage.removeItem('cloveraiToken')
    return false
  }

  return true
}

const DefaultLayout = React.lazy(() => import('../layout/Default'))

const PrivateRoutes = () =>
  isAuthenticated() ? <Outlet /> : <Navigate to="/login" />

const App = () => {
  const [, setAuth] = useState(isAuthenticated())

  useEffect(() => {
    const checkAuth = () => {
      const now = moment().valueOf()
      const storage = JSON.parse(localStorage.getItem('cloveraiToken')) || null
      const token = localStorage.getItem('token')

      // Check JWT token
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]))
          if (payload.exp * 1000 > Date.now()) {
            setAuth(true)
            return
          }
          localStorage.removeItem('token')
          localStorage.removeItem('user')
        } catch (e) {
          localStorage.removeItem('token')
          localStorage.removeItem('user')
        }
      }

      // Check legacy token
      if (!storage || now > storage?.expires) {
        localStorage.removeItem('cloveraiToken')
        setAuth(false)
      } else {
        setAuth(true)
      }
    }

    checkAuth()

    const interval = setInterval(checkAuth, 60000)

    return () => clearInterval(interval)
  }, [])

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <HashRouter
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route element={<PrivateRoutes />}>
                <Route path="*" name="Home" element={<DefaultLayout />} />
              </Route>

              <Route path="/login" element={<Login />} exact />
              <Route path="/register" element={<Register />} exact />
              <Route
                path="/recover-password"
                element={<RecoverPassword />}
                exact
              />
              <Route path="/lock-screen" element={<LockScreen />} exact />
              <Route
                path="/confirm-email"
                element={<ConfirmEmail />}
                exact
              />
              <Route
                path="/email-verification"
                element={<EmailVerification />}
                exact
              />
              <Route
                path="/verify-email"
                element={<VerifyEmail />}
                exact
              />
              <Route
                path="/reset-password"
                element={<ResetPassword />}
                exact
              />
              <Route
                path="/resend-verification"
                element={<ResendVerification />}
                exact
              />
              <Route
                path="/accept-invite"
                element={<AcceptInvite />}
                exact
              />
              <Route path="/page-404" element={<Page404 />} exact />
              <Route path="/page-500" element={<Page500 />} exact />
              <Route path="/support" element={<Support />}>
                <Route
                  path="getting-started"
                  element={<GettingStarted />}
                />
                <Route
                  path="product-specifications"
                  element={<ProductSpecifications />}
                />
                <Route
                  path="installation-and-configuration-instructions"
                  element={<InstallationInstructions />}
                />
                <Route
                  path="server-requirements"
                  element={<ServerRequirements />}
                />
                <Route
                  path="troubleshooting-tips"
                  element={<TroubleshootingTips />}
                />
                <Route path="faqs" element={<FAQs />} />
                <Route
                  path="regular-updates"
                  element={<RegularUpdates />}
                />
              </Route>
            </Routes>
          </Suspense>
        </HashRouter>
      </ThemeProvider>
    </ErrorBoundary>
  )
}

export default App
